import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import DatePicker from 'react-datepicker'
import { CaretDownIcon } from '@phosphor-icons/react'
import { AllGuildTypes, Role } from '../../types/Roles'
import { User } from '../../types/User'
import { getEmailError, validateEmail } from '../../utils/validations'
import { DateFieldInput } from './DateFieldInput'
import { FormField, FormTextField } from './FormTextField'

export type GeneralInformationData = {
  email: string
  firstName: string
  lastName: string
  startDate: Date | null
  endDate: Date | null
  selectedGuild: number
}

export type GeneralInformationSectionRef = {
  getData: () => GeneralInformationData
}

interface GeneralInformationSectionProps {
  guilds: Role[]
  user?: User
  displayErrorBanner?: boolean
  onChange?: (data: GeneralInformationData) => void
  onGuildChange?: (guildId: number) => void
}

export const GeneralInformationSection = forwardRef<
  GeneralInformationSectionRef,
  GeneralInformationSectionProps
>(function GeneralInformationSection(
  { guilds, user, displayErrorBanner, onChange, onGuildChange },
  ref,
) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [selectedGuild, setSelectedGuild] = useState(-1)
  const [touched, setTouched] = useState({
    email: false,
    firstName: false,
    lastName: false,
    startDate: false,
    endDate: false,
    guild: false,
  })

  const getData = (): GeneralInformationData => ({
    email,
    firstName,
    lastName,
    startDate,
    endDate,
    selectedGuild,
  })

  useImperativeHandle(ref, () => ({
    getData,
  }))

  useEffect(() => {
    onChange?.(getData())
  }, [email, firstName, lastName, startDate, endDate, selectedGuild])

  useEffect(() => {
    if (!user) return
    setEmail(user.email)
    setFirstName(user.firstName)
    setLastName(user.lastName)
    setStartDate(user.start_on ? new Date(user.start_on) : null)
    setEndDate(user.end_on ? new Date(user.end_on) : null)
    setSelectedGuild(user.guildId)
  }, [user])

  const markTouched = (field: keyof typeof touched) => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const showFieldError = (field: keyof typeof touched) =>
    touched[field] || Boolean(displayErrorBanner)

  const emailError = showFieldError('email')
    ? getEmailError(email, { required: true })
    : email
    ? getEmailError(email)
    : null
  const firstNameError =
    showFieldError('firstName') && !firstName.trim()
      ? 'First name is required'
      : null
  const lastNameError =
    showFieldError('lastName') && !lastName.trim()
      ? 'Last name is required'
      : null
  const startDateError =
    showFieldError('startDate') && !startDate ? 'Start date is required' : null
  const guildError =
    showFieldError('guild') && selectedGuild === -1 ? 'Guild is required' : null

  const handleGuildChange = (guildId: number) => {
    setSelectedGuild(guildId)
    markTouched('guild')
    onGuildChange?.(guildId)
  }

  return (
    <section className="employee-form__section">
      <div className="employee-form__section-heading">
        <div className="employee-form__section-header">
          <h3 className="employee-form__section-title">General Information</h3>
        </div>
        <p className="employee-form__required-note">(*) Required</p>
      </div>
      <div className="employee-form__grid employee-form__grid--3">
        <FormTextField
          label="Email"
          required
          type="email"
          id="createEmployeeEmailInput"
          value={email}
          disabled={!!user}
          onChange={e => setEmail(e.target.value)}
          onBlur={() => markTouched('email')}
          aria-label="login-email-input"
          placeholder="user@acklenavenue.com"
          error={emailError}
          success={touched.email && validateEmail(email)}
        />
        <FormTextField
          label="First Name"
          required
          type="text"
          id="createEmployeeFirstNameInput"
          value={firstName}
          onChange={e => setFirstName(e.target.value || '')}
          onBlur={() => markTouched('firstName')}
          aria-label="login-first-name-input"
          maxLength={25}
          error={firstNameError}
          success={touched.firstName && Boolean(firstName.trim())}
        />
        <FormTextField
          label="Last Name"
          required
          type="text"
          id="createEmployeeLastNameInput"
          value={lastName}
          onChange={e => setLastName(e.target.value || '')}
          onBlur={() => markTouched('lastName')}
          aria-label="login-last-name-input"
          maxLength={25}
          error={lastNameError}
          success={touched.lastName && Boolean(lastName.trim())}
        />
      </div>
      <div className="employee-form__grid employee-form__grid--3">
        <FormField label="Start Date" required error={startDateError}>
          <DatePicker
            selected={startDate}
            maxDate={endDate}
            placeholderText="Select a date"
            customInput={
              <DateFieldInput
                success={touched.startDate && !!startDate && !startDateError}
                error={Boolean(startDateError)}
              />
            }
            onChange={date => {
              markTouched('startDate')
              if (date) {
                if (endDate && date > endDate) {
                  setEndDate(date)
                }
                setStartDate(date)
              }
            }}
            onCalendarClose={() => markTouched('startDate')}
          />
        </FormField>
        <FormField label="End Date">
          <DatePicker
            selected={endDate}
            minDate={startDate}
            placeholderText="Select a date"
            customInput={
              <DateFieldInput
                success={touched.endDate && !!endDate}
                clearable
                onClear={() => {
                  markTouched('endDate')
                  setEndDate(null)
                }}
              />
            }
            onChange={date => {
              markTouched('endDate')
              if (date) {
                if (!startDate) {
                  setEndDate(date)
                } else if (date < startDate) {
                  setEndDate(startDate)
                } else {
                  setEndDate(date)
                }
              } else {
                setEndDate(null)
              }
            }}
            onCalendarClose={() => markTouched('endDate')}
          />
        </FormField>
        <FormField
          label="Guild"
          htmlFor="createEmployeeGuildInput"
          required
          error={guildError}
        >
          <div className="employee-form__select-wrapper">
            <select
              className={`employee-form__select-input ${
                guildError ? 'employee-form__select-input--error' : ''
              } ${
                touched.guild && selectedGuild !== -1 && !guildError
                  ? 'employee-form__select-input--success'
                  : ''
              }`}
              value={selectedGuild === -1 ? '' : selectedGuild}
              id="createEmployeeGuildInput"
              aria-invalid={Boolean(guildError)}
              aria-describedby={
                guildError ? 'createEmployeeGuildInput-error' : undefined
              }
              onBlur={() => markTouched('guild')}
              onChange={e => handleGuildChange(Number(e.target.value))}
            >
              <option hidden value="">
                Select a guild
              </option>
              {guilds
                .filter(guild => guild.type !== AllGuildTypes.GuildMaster)
                .map((guild, index) => (
                  <option key={`guild-${index}`} value={guild.id}>
                    {guild.role}
                  </option>
                ))}
            </select>
            <span className="employee-form__select-icon">
              <CaretDownIcon size={14} weight="regular" />
            </span>
          </div>
        </FormField>
      </div>
    </section>
  )
})
